import { memo, useMemo, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { QueryField, QueryRule } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import { isRuleValid } from '../../../../../utils/queryBuilderUtils';
import { groupFieldsBySubcategory } from './fieldConfig';
import { buildSummaryField, HIDDEN_FROM_SUMMARY_FIELD_NAMES, SummaryField } from './summaryBuilders';

interface SelectionSummaryProps {
    fields: QueryField[];
    rules: QueryRule[];
}

// Au-delà de ce nombre de valeurs, la liste se replie derrière un "Voir plus" plutôt que
// d'étirer indéfiniment la carte de résumé (ex: une liste de 198 magasins sélectionnés).
const COLLAPSED_VALUES_LIMIT = 6;

function ValueList({ values, idPrefix }: { values: string[]; idPrefix: string }) {
    const { t } = useLanguage();
    const [expanded, setExpanded] = useState(false);
    const isCollapsible = values.length > COLLAPSED_VALUES_LIMIT;
    const visibleValues = expanded || !isCollapsible ? values : values.slice(0, COLLAPSED_VALUES_LIMIT);

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.25, pl: 2 }}>
            {visibleValues.map((value, index) => (
                <Typography key={`${idPrefix}-${index}`} variant="body2" sx={{ color: '#495057' }}>
                    {value}
                </Typography>
            ))}
            {isCollapsible && (
                <Button
                    size="small"
                    onClick={() => setExpanded(v => !v)}
                    sx={{ textTransform: 'none', fontSize: '0.78rem', color: '#3949ab', p: 0, minWidth: 0, flexBasis: '100%', justifyContent: 'flex-start', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                >
                    {expanded
                        ? t('pages.clients.queryBuilder.simple.summary.showLess')
                        : t('pages.clients.queryBuilder.simple.summary.showMore', { count: values.length - COLLAPSED_VALUES_LIMIT })}
                </Button>
            )}
        </Box>
    );
}

function SummaryItem({ item }: { item: SummaryField }) {
    return (
        <Box sx={{ mt: 1 }}>
            {item.label && (
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#343a40' }}>
                    {item.label}
                </Typography>
            )}
            {item.intro && (
                <Typography variant="body2" sx={{ color: '#495057' }}>
                    {item.intro}
                </Typography>
            )}
            <ValueList values={item.values} idPrefix={`${item.id}-values`} />
            {item.secondIntro && (
                <Typography variant="body2" sx={{ color: '#495057', mt: 0.5 }}>
                    {item.secondIntro}
                </Typography>
            )}
            {item.secondValues && <ValueList values={item.secondValues} idPrefix={`${item.id}-second`} />}
        </Box>
    );
}

const SelectionSummary = memo(function SelectionSummary({ fields, rules }: SelectionSummaryProps) {
    const { t, formatLocale } = useLanguage();

    const summaryGroups = useMemo(
        () => groupFieldsBySubcategory(fields)
            .map(({ titleKey, fields: groupFields }) => ({
                titleKey,
                items: groupFields
                    .filter(field => !HIDDEN_FROM_SUMMARY_FIELD_NAMES.has(field.name))
                    .map(field => {
                        const rule = rules.find(r => r.field === field.name);
                        return rule && isRuleValid(rule) ? buildSummaryField(field, rule, t, formatLocale, fields, rules) : null;
                    })
                    .filter((item): item is SummaryField => item !== null),
            }))
            .filter(group => group.items.length > 0),
        [fields, rules, t, formatLocale]
    );

    return (
        <Box sx={{ mt: 2, mb: 2, p: 1.5, bgcolor: '#eef1fd', border: '1px solid #c7d0f8', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#3949ab' }}>
                {t('pages.clients.queryBuilder.simple.summary.title')}
            </Typography>
            {summaryGroups.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#6c757d', mt: 0.5 }}>
                    {t('pages.clients.queryBuilder.simple.summary.empty')}
                </Typography>
            ) : (
                <Box
                    sx={{
                        mt: 1,
                        columnCount: { xs: 1, md: 2, xl: 3 },
                        columnGap: '20px',
                    }}
                >
                    {summaryGroups.map(group => (
                        <Box
                            key={group.titleKey}
                            sx={{ breakInside: 'avoid', WebkitColumnBreakInside: 'avoid', mb: 2.5 }}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#3949ab' }}>
                                {t(group.titleKey)}.
                            </Typography>
                            {group.items.map(item => <SummaryItem key={item.id} item={item} />)}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
});

export default SelectionSummary;
